/**
 * IMPORTANT: These colors must jive with the colors in scss/_colors.scss.
 * If you are adding a color there, you must add it here as well.
 *
 * Notes in terms of theming:
 * - For the foreseeable future, the primary difference between ADC and SS will be the
 *   default primary button blue.
 * - All other colors will be used universally across both ADC and SS.
 */
export const colorVars = {
  // branding
  utkColorAdcDarkGreen: '#297708',
  utkColorAdcLightGreen: '#77bc1f',
  utkColorAdcLightBlue: '#57c1e8',

  // ADC Primary Button
  utkColorAdcBlueDark: '#045489',
  utkColorAdcBlue: '#036eb5',
  utkColorAdcBlueActiveMedium: '#7ac6e2',
  utkColorAdcBlueMedium: '#aee0f2',
  utkColorAdcBlueLight: '#e4f7fd',
  utkColorAdcBlueExtraLight: '#f0fbfe',

  // Smartsale Primary Button
  utkColorSmartBlueDark: '#2c5cb5',
  utkColorSmartBlue: '#3876e7',
  utkColorSmartBlueActiveMedium: '#6295f3',
  utkColorSmartBlueMedium: '#8bb4ff',
  utkColorSmartBlueLight: '#d4e3ff',
  utkColorSmartBlueExtraLight: '#e5f3ff',

  // color roles
  // info
  utkColorInfoDark: '#045489',
  utkColorInfo: '#036eb5',
  utkColorInfoActiveMedium: '#7ac6e2',
  utkColorInfoMedium: '#aee0f2',
  utkColorInfoLight: '#e4f7fd',
  utkColorInfoExtraLight: '#f0fbfe',
  // success
  utkColorSuccessDark: '#1d5d02',
  utkColorSuccess: '#297708',
  utkColorSuccessActiveMedium: '#a5cd75',
  utkColorSuccessMedium: '#b8dc8f',
  utkColorSuccessLight: '#d5e8bf',
  utkColorSuccessExtraLight: '#edf3e7',
  // caution
  utkColorCautionDark: '#cb3c0e',
  utkColorCaution: '#f15c2c',
  utkColorCautionActiveMedium: '#fe9f81',
  utkColorCautionMedium: '#fcc4B3',
  utkColorCautionLight: '#fddacf',
  utkColorCautionExtraLight: '#fef0eb',
  // error
  utkColorErrorDark: '#a10000',
  utkColorError: '#c30000',
  utkColorErrorActiveMedium: '#e36c6c',
  utkColorErrorMedium: '#f09c9c',
  utkColorErrorLight: '#f2c5c5',
  utkColorErrorExtraLight: '#fce4e5',

  // Alt button colors
  utkColorPurpleDark: '#541f61',
  utkColorPurple: '#6a277a',
  utkColorPurpleActiveMedium: '#ae78ba',
  utkColorPurpleMedium: '#daa8e5',
  utkColorPurpleLight: '#f2d2f9',
  utkColorPurpleExtraLight: '#fbecfe',

  // grayscale
  utkColorBlack: '#000102',
  utkColorNearBlack: '#333',
  utkColorGrayDark: '#263238',
  utkColorGray: '#5c676e',
  utkColorGrayMedium: '#97a0a6',
  utkColorGrayMediumLight: '#c0c8cc',
  utkColorGrayLight: '#dae0e3',
  utkColorGrayExtraLight: '#e9ecee',
  utkColorOffWhiteDark: '#f0f2f4',
  utkColorOffWhite: '#f8f9fa',
  utkColorWhite: '#fff',

  // misc
  utkColorGraySemantic: '#d3d3d3',
  utkColorAdcLightPurple: '#a639bc',
  utkColorAdcDarkGreen60: 'rgba(41, 119, 8, .6)',
  utkColorAdcLightGreen60: 'rgba(119, 188, 31, .6)',
  utkColorInfo60: 'rgba(3, 110, 181, .6)',
  utkColorBlack60: 'rgba(0, 1, 2, .6)',
  utkColorGrayDark60: 'rgba(38, 50, 56, .6)',
  utkColorDarkCobalt: '#28397f',
  utkColorSunYellow: '#fed000',
  utkColorSalmon: '#eb5353',

  // accents
  utkColorNavyBlue: '#345771',
  utkColorAquaBlue: '#377a93',
  utkColorCloudBlue: '#f5faff',

  utkColorTransparent: 'transparent',
}

export enum ColorNames {
  // adc branding
  AdcDarkGreen = 'adcDarkGreen',
  AdcLightGreen = 'adcLightGreen',
  AdcLightBlue = 'adcLightBlue',
  // ADC Primary Button
  AdcBlueDark = 'adcBlueDark',
  AdcBlue = 'adcBlue',
  AdcBlueActiveMedium = 'adcBlueActiveMedium',
  AdcBlueMedium = 'adcBlueMedium',
  AdcBlueLight = 'adcBlueLight',
  AdcBlueExtraLight = 'adcBlueExtraLight',
  // Smartsale Primary Button
  SmartBlueDark = 'smartBlueDark',
  SmartBlue = 'smartBlue',
  SmartBlueActiveMedium = 'smartBlueActiveMedium',
  SmartBlueMedium = 'smartBlueMedium',
  SmartBlueLight = 'smartBlueLight',
  SmartBlueExtraLight = 'smartBlueExtraLight',

  // color roles
  InfoDark = 'infoDark',
  Info = 'info',
  InfoActiveMedium = 'infoActiveMedium',
  InfoMedium = 'infoMedium',
  InfoLight = 'infoLight',
  InfoExtraLight = 'infoExtraLight',
  // success
  SuccessDark = 'successDark',
  Success = 'success',
  SuccessActiveMedium = 'successActiveMedium',
  SuccessMedium = 'successMedium',
  SuccessLight = 'successLight',
  SuccessExtraLight = 'successExtraLight',
  // caution
  CautionDark = 'cautionDark',
  Caution = 'caution',
  CautionActiveMedium = 'cautionActiveMedium',
  CautionMedium = 'cautionMedium',
  CautionLight = 'cautionLight',
  CautionExtraLight = 'cautionExtraLight',
  // error
  ErrorDark = 'errorDark',
  Error = 'error',
  ErrorActiveMedium = 'errorActiveMedium',
  ErrorMedium = 'errorMedium',
  ErrorLight = 'errorMediumLight',
  ErrorExtraLight = 'errorLight',
  // alt button colors
  PurpleDark = 'purpleDark',
  Purple = 'purple',
  PurpleActiveMedium = 'purpleActiveMedium',
  PurpleMedium = 'purpleMedium',
  PurpleLight = 'purpleLight',
  PurpleExtraLight = 'purpleExtraLight',
  // grayscale
  Black = 'black',
  NearBlack = 'nearBlack',
  GrayDark = 'grayDark',
  Gray = 'gray',
  GrayMedium = 'grayMedium',
  GrayMediumLight = 'grayMediumLight',
  GrayLight = 'grayLight',
  GrayExtraLight = 'grayExtraLight',
  OffWhiteDark = 'offWhiteDark',
  OffWhite = 'offWhite',
  White = 'white',
  // misc
  GraySemantic = 'graySemantic',
  AdcLightPurple = 'adcLightPurple',
  AdcDarkGreen60 = 'adcDarkGreen60',
  AdcLightGreen60 = 'adcLightGreen60',
  Info60 = 'info60',
  Black60 = 'black60',
  GrayDark60 = 'grayDark60',
  DarkCobalt = 'darkCobalt',
  SunYellow = 'sunYellow',
  Salmon = 'salmon',

  // accents
  NavyBlue = 'navyBlue',
  AquaBlue = 'aquaBlue',
  CloudBlue = 'cloudBlue',

  Transparent = 'transparent',
}
